import React, { memo, useContext } from "react";
import { ReactNode } from "react";
import {
  GelContainerLite,
  GelRowLayout,
  GelLabel,
  GelForm,
  GelFormField,
  GelButton,
  GelBoxLayout,
  GelScreenDetectorContext
} from '@tal-gel/components';
import { getGelTokens } from '@tal-gel/theming';
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { AdobeClickType } from "../../constants/constants";
import { useCookies } from "react-cookie";
import { AdobeAnalytics } from "../analytics/adobe-analytics";
import { MMCTooltip } from "./tooltip";

interface ICardProps {
  title: string;
  subTitle?: string;
  icon?: ReactNode;
  buttonText?: string;
  children: ReactNode;
  showFooter: boolean;
  whiteHeader?: boolean;
  lightHeader?: boolean;
  claimAssesmentStatus?: ReactNode;
  claimNumber?: string;
  style?: React.CSSProperties;
  toolTip?: string;
}

const Card: React.FC<ICardProps> = ({ title, subTitle, buttonText, children, showFooter, whiteHeader, lightHeader, claimNumber, style = {}, toolTip }) => {
  const { global: { sizeNone, sizeBase, sizeBaseUnit, sizeBaseX5, themeColorBackgroundDefault, fontFamilySans,
    sizeBaseX4, themeColorBorderDefault, sizeBaseX2, themeColorGrayT05, themeColorBackgroundInverse, themeColorGrayT90 } } = getGelTokens();

  const { screen, isXsScreen } = useContext(GelScreenDetectorContext) as GelScreenDetectorContext;
    
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setClaimCookie] = useCookies(['selectedClaim']);
  const [{ tpid }] = useCookies(['tpid']);


  const onSubmit = () => {
    setClaimCookie('selectedClaim', claimNumber);

    AdobeAnalytics.PushClickEvent(tpid, buttonText!, AdobeClickType.Button)
    navigate("/claimdetails", { state: { claimNumber } });
  }

  const Header = styled.div(() => ({
    minHeight: sizeBaseUnit * 15,
    color: themeColorBackgroundDefault,
    fontSize: 10,
    fontFamily: fontFamilySans,
    padding: sizeBaseX4,
    borderRadius: `${sizeBaseUnit * 2}px ${sizeBaseUnit * 2}px 0px 0px`,
    margin: "0px"
  }));

  const Body = styled.div(() => ({
    color: themeColorBackgroundDefault,
    border: "0px solid",
    borderColor: themeColorBorderDefault,
    borderRadius: `0px 0px ${sizeBaseUnit * 2}px ${sizeBaseUnit * 2}px`
  }));

  const Footer = styled.div(() => ({
    height: sizeBaseX2 * 10,
    color: themeColorBackgroundDefault,
    paddingTop: sizeBaseX2
  }));


  return (
    <GelContainerLite gutter="medium" style={{
      backgroundColor: themeColorBackgroundDefault,
      border: "1px solid",
      borderColor: themeColorBorderDefault,
      padding: "0px",
      borderRadius: sizeBaseUnit * 2,
      ...style
    }}
    >
      <Header style={{
        backgroundColor: whiteHeader
          ? undefined
          : (lightHeader ? themeColorGrayT05 : themeColorBackgroundInverse),
        borderBottom: whiteHeader ? `1px solid ${themeColorBorderDefault}` : "none"
      }}>
        <GelRowLayout gutter="small">

          <h1 style={{
            margin: "0px",
            color: (lightHeader || whiteHeader) ? themeColorGrayT90 : themeColorBackgroundDefault,
          }}>{title}</h1>

          <div style={{ display: "flex", alignItems: "center" }}>
            {toolTip &&
              <MMCTooltip selfAdaptive style={{ padding: isXsScreen(screen) ? "6px 4px 4px 0px" : "4px 4px 4px 0px", marginRight: "4px" }}>
                {toolTip}
              </MMCTooltip>}

            {subTitle &&
              <GelLabel style={{
                fontWeight: 10,
                display: "inline-block",
                color: (lightHeader || whiteHeader) ? themeColorGrayT90 : themeColorBackgroundDefault
              }}>{subTitle}</GelLabel>
            }
          </div>
        </GelRowLayout>
      </Header>

      <Body>{children}</Body>

      {showFooter &&
        <Footer style={{ padding: `${sizeBaseX5}px ${sizeBase * 2.5}px 0px ${sizeBase * 2.5}px`, boxSizing: 'content-box' }}>
          <GelForm
            labelAtTop={true}
            onSubmit={onSubmit}>
            <GelFormField width="auto">
              <GelButton style={{
                width: "100%",
              }}
                name=""
                primary large submit>
                {buttonText}
              </GelButton>
            </GelFormField>
          </GelForm>
        </Footer>}
    </GelContainerLite>
  );
}


export default memo(Card);